<template>
  <v-tooltip
    :disabled="tooltipDisabled"
    bottom>
    <template v-slot:activator="{ on, attrs }">
      <div
        v-on="on"
        v-bind="attrs"
        @click.capture="onClickCapture">
        <v-select
          ref="selectInput"
          outlined
          v-bind="selectAttrs"
          v-on="mergeListeners(selectListeners)"
          :value="value"
          :menu-props="mergedMenuProps"
          :append-icon="hideIcon ? '' : 'mdi-chevron-down'"
          :color="defaultColor"
          :persistent-placeholder="persistentPlaceholder"
          :class="inputClass"
          :style="inputStyle"
          @input="$emit('input', $event)">
          <template v-if="$scopedSlots.selection" v-slot:selection="slotProps">
            <slot name="selection" v-bind="slotProps" />
          </template>
          <template v-else-if="hasChips" v-slot:selection="slotProps">
            <v-tooltip
              bottom
              :color="defaultColor"
              :disabled="isSelectionTooltipDisabled(slotProps)">
              <template v-slot:activator="{ on: tooltipChipListeners, attrs: tooltipChipAttrs }">
                <v-chip
                  class="chip-select-content"
                  color="primary"
                  v-bind="mergeAttrs(slotProps.attrs, tooltipChipAttrs)"
                  :input-value="slotProps.selected"
                  :disabled="slotProps.disabled"
                  :close="hasDeletableChips && !slotProps.disabled"
                  :small="hasSmallChips"
                  @click="slotProps.select"
                  @click:close="onChipClose(slotProps)"
                  v-on="tooltipChipListeners">
                  <span class="chip-select-line">
                    {{ getSelectionText(slotProps) }}
                  </span>
                </v-chip>
              </template>
              <span>
                {{ getSelectionText(slotProps) }}
              </span>
            </v-tooltip>
          </template>
          <template v-if="$scopedSlots.item" v-slot:item="slotProps">
            <slot name="item" v-bind="slotProps" />
          </template>
        </v-select>
      </div>
    </template>
    <span>
      {{ tooltipText }}
    </span>
  </v-tooltip>
</template>

<script>
import Formatters from '@/shared/formatters/formatters';
export default {
  name: "Select",

  inheritAttrs: false,

  props: {
    value: [String, Number, Boolean, Object, Array],

    regular: {
      type: Boolean,
      default: false,
    },

    hideIcon: {
      type: Boolean,
      default: false,
    },

    defaultColor: {
      type: String,
      default: 'primary',
    },

    textColor: {
      type: String,
      default: '',
    },

    persistentPlaceholder: {
      type: Boolean,
      default: true,
    },

    borderSelected: {
      type: Boolean,
      default: false,
    },

    showTooltip: {
      type: Boolean,
      default: false,
    },

    tooltipValue: {
      default: false,
    },
  },

  computed: {
    selectListeners() {
      const listeners = Object.assign({}, this.$listeners);
      delete listeners.click;
      return listeners;
    },

    selectAttrs() {
      const attrs = { ...this.$attrs };
      delete attrs.menuProps;
      delete attrs['menu-props'];
      return attrs;
    },

    mergedMenuProps() {
      const fromAttrs = this.$attrs.menuProps || this.$attrs['menu-props'] || {};

      return {
        offsetY: true,
        'offset-y': true,
        nudgeBottom: 4,
        'nudge-bottom': 4,
        ...fromAttrs,
      };
    },

    mainClass() {
      return {
        'select-default': true,
        'select-border-selected': this.hasValue && this.borderSelected,
        'select-persist-placeholder': this.persistentPlaceholder,
        'select-regular': this.regular,
        'select-text-color': this.textColor !== '',
      };
    },

    inputClass() {
      const attrs = this.$attrs;
      const data = this.$vnode && this.$vnode.data;
      const classes = [];
      if (data) {
        if (data.staticClass) classes.push(data.staticClass);
        if (data.class) classes.push(data.class);
      }
      return [
        this.mainClass,
        attrs.class,
        ...classes,
      ];
    },

    inputStyle() {
      const attrs = this.$attrs;
      const data = this.$vnode && this.$vnode.data;
      const styles = [];
      if (data) {
        if (data.staticStyle) styles.push(data.staticStyle);
        if (data.style) styles.push(data.style);
      }
      if (this.textColor !== '') {
        styles.push({ '--select-text-color': `var(--v-${this.textColor}-base)` });
      }
      return [
        attrs.style,
        ...styles,
      ];
    },

    hasValue() {
      const formatter = new Formatters();

      if (Array.isArray(this.value) && this.value.length > 0) return true;
      if (Array.isArray(this.value) && this.value.length === 0) return false;
      if (formatter.isEmptyValue(this.value)) return false;
      return true;
    },

    tooltipSourceValue() {
      return this.tooltipValue !== false ? this.tooltipValue : this.value;
    },

    tooltipText() {
      return this.formatTooltipValue(this.tooltipSourceValue);
    },

    tooltipDisabled() {
      return !this.showTooltip || this.isTooltipEmpty(this.tooltipSourceValue);
    },

    hasChips() {
      return Object.prototype.hasOwnProperty.call(this.$attrs, 'chips') && this.$attrs.chips !== false;
    },

    hasSmallChips() {
      return Object.prototype.hasOwnProperty.call(this.$attrs, 'small-chips') && this.$attrs['small-chips'] !== false;
    },

    hasDeletableChips() {
      return Object.prototype.hasOwnProperty.call(this.$attrs, 'deletable-chips') && this.$attrs['deletable-chips'] !== false;
    },
  },

  methods: {
    onClickCapture(event) {
      this.openMenu();
      this.$emit('click', event);
    },

    focus() {
      const select = this.$refs.selectInput;
      if (select && typeof select.focus === 'function') {
        select.focus();
      }
    },

    openMenu() {
      const select = this.$refs.selectInput;
      if (!select) return;
      if (typeof select.activateMenu === 'function') {
        select.activateMenu();
        return;
      }
      if (typeof select.isMenuActive !== 'undefined') {
        select.isMenuActive = true;
        return;
      }
      if (typeof select.menu !== 'undefined') {
        select.menu = true;
      }
    },

    mergeListeners(listeners, tooltipOn) {
      const merged = Object.assign({}, listeners);
      if (!tooltipOn) return merged;
      Object.keys(tooltipOn).forEach((event) => {
        const current = merged[event];
        if (current) {
          merged[event] = Array.isArray(current) ? current.concat(tooltipOn[event]) : [current, tooltipOn[event]];
          return;
        }
        merged[event] = tooltipOn[event];
      });
      return merged;
    },

    mergeAttrs(attrs, tooltipAttrs) {
      return Object.assign({}, attrs, tooltipAttrs);
    },

    formatTooltipValue(value) {
      if (Array.isArray(value)) {
        return value
          .map((item) => this.formatSingleTooltipValue(item))
          .filter((item) => item !== '')
          .join(', ');
      }
      return this.formatSingleTooltipValue(value);
    },

    formatSingleTooltipValue(value) {
      if (this.isTooltipEmpty(value)) return '';
      if (value && typeof value === 'object') {
        return this.formatPrimitiveTooltipValue(this.getItemText(value));
      }
      const itemFromList = this.findItemFromItems(value);
      if (itemFromList) {
        return this.formatPrimitiveTooltipValue(this.getItemText(itemFromList));
      }
      return this.formatPrimitiveTooltipValue(value);
    },

    formatPrimitiveTooltipValue(value) {
      if (this.isTooltipEmpty(value)) return '';
      if (typeof value === 'string') return value;
      if (typeof value === 'number' || typeof value === 'boolean') return String(value);
      if (value && typeof value === 'object') {
        try {
          return JSON.stringify(value);
        } catch (error) {
          return String(value);
        }
      }
      return String(value);
    },

    getItemText(item) {
      const itemText = this.$attrs['item-text'] || this.$attrs.itemText;
      if (typeof itemText === 'function') {
        return itemText(item);
      }
      if (itemText && item && typeof item === 'object' && itemText in item) {
        return item[itemText];
      }
      if (item && typeof item === 'object') {
        if ('text' in item) return item.text;
        if ('name' in item) return item.name;
        if ('label' in item) return item.label;
        if ('description' in item) return item.description;
      }
      return item;
    },

    getItemValue(item) {
      const itemValue = this.$attrs['item-value'] || this.$attrs.itemValue;
      if (typeof itemValue === 'function') {
        return itemValue(item);
      }
      if (itemValue && item && typeof item === 'object' && itemValue in item) {
        return item[itemValue];
      }
      if (item && typeof item === 'object') {
        if ('value' in item) return item.value;
        if ('id' in item) return item.id;
      }
      return item;
    },

    findItemFromItems(value) {
      const items = this.$attrs.items;
      if (!Array.isArray(items)) return null;
      return items.find((item) => {
        if (item && typeof item === 'object') {
          return this.getItemValue(item) === value;
        }
        return item === value;
      });
    },

    isTooltipEmpty(value) {
      const formatter = new Formatters();
      if (Array.isArray(value)) return value.length === 0;
      if (value && typeof value === 'object') return Object.keys(value).length === 0;
      return formatter.isEmptyValue(value);
    },

    getSelectionItem(slotProps) {
      if (slotProps && typeof slotProps === 'object' && 'item' in slotProps) return slotProps.item;
      return slotProps;
    },

    getSelectionText(slotProps) {
      return this.formatSingleTooltipValue(this.getSelectionItem(slotProps));
    },

    isSelectionTooltipDisabled(slotProps) {
      return this.isTooltipEmpty(this.getSelectionText(slotProps));
    },

    onChipClose(slotProps) {
      if (!slotProps || !slotProps.parent) return;
      const item = this.getSelectionItem(slotProps);
      if (typeof slotProps.parent.onChipInput === 'function') {
        slotProps.parent.onChipInput(item);
        return;
      }
      if (typeof slotProps.parent.selectItem === 'function') {
        slotProps.parent.selectItem(item);
      }
    },
  }

}
</script>

<style lang="scss">
.select-default :is([class*="v-input__prepend"], [class*="v-input__append"]) {
  height: 100% !important;
  display: flex !important;
  align-items: center !important;
}

.select-default .v-input__prepend-outer,
.select-default .v-input__prepend-inner,
.select-default .v-input__append-inner,
.select-default .v-input__append-outer,
.select-default .v-input__prepend-outer,
.select-default .v-input__prepend-inner,
.select-default .v-input__append-inner,
.select-default .v-input__append-outer {
  margin-top: 0 !important;
  height: 100% !important;
  align-self: center !important;
}

.select-default .v-text-field__details {
  margin-bottom: 5px !important;
}

.select-default:not(:is([class*="field--disabled"])):is([class*="v-input--is-disabled"])
  > .v-input__control
    > .v-input__slot
      fieldset {
  color: color-mix(in srgb, var(--v-border-base) 40%, transparent) !important;
}

.select-default:not(:is([class*="field--disabled"])):is([class*="v-input--is-disabled"])
  > .v-input__control
    > .v-input__slot {
      label,
      input,
      i {
        color: color-mix(in srgb, var(--v-foreground-base) 40%, transparent) !important;
      }
}

.select-default:not(.select-border-selected):not(.v-input--is-focused):not(.v-input--has-state):not(:is([class*="field--disabled"], [class*="v-input--is-disabled"]))
  > .v-input__control
    > .v-input__slot
      fieldset {
  color: var(--v-border-base) !important;
}

.select-border-selected:not(.v-input--is-focused):not(.v-input--has-state)
  > .v-input__control
    > .v-input__slot
      fieldset {
  color: var(--v-primary-base) !important;
}

.select-default fieldset {
  border-width: 2px;
}

.select-default {
  > .v-input__control {
    ::before {
        border-image: none !important;
      }

    > .v-select__slot,
    > .v-input__slot {
      padding: 0 12px !important;
      min-height: 50px;
      align-items: center !important;

      > .v-select__selections {
        margin: 0 !important;
        height: 100%;
      }

      &::before,
      &::after,
      :before,
      :after {
        border-color: transparent !important;
        background-color: transparent !important;
      }

      label {
        padding: 0 8px;
        font-size: 16px;
        color: var(--v-foreground-base);
        max-width: 133%;
        line-height: 1;
      }
    }
  }
}

.select-default:not(.v-input--has-state) .v-select__selection {
  color: var(--v-foreground-base);
}

.select-regular {
  margin: 0px !important;
  padding: 0px !important;

  > .v-input__control {
    > .v-input__slot {
      margin-bottom: 1px;
    }
  }
}

.select-default:not(.select-persist-placeholder).v-input--is-label-active:not(.v-input--is-focused):not(.v-select--is-menu-active) {
  fieldset legend {
    max-width: 0.01px !important;
    overflow: hidden;
    padding: 0 !important;
  }

  > .v-input__control > .v-input__slot label,
  > .v-input__control > .v-select__slot label {
    opacity: 0;
    pointer-events: none;
    transform: translateY(0) scale(1) !important;
  }
}

.select-default:not(.select-persist-placeholder).v-input--is-label-active {
  > .v-input__control > .v-input__slot label,
  > .v-input__control > .v-select__slot label {
    transition: transform 0.15s cubic-bezier(0.4, 0, 0.2, 1),
      opacity 0.15s cubic-bezier(0.4, 0, 0.2, 1),
      color 0.15s cubic-bezier(0.4, 0, 0.2, 1) !important;
  }
}

.select-default:not(.select-persist-placeholder).v-input--is-label-active.v-input--is-focused,
.select-default:not(.select-persist-placeholder).v-input--is-label-active.v-select--is-menu-active {
  > .v-input__control > .v-input__slot label,
  > .v-input__control > .v-select__slot label {
    background-color: var(--v-muted-base);
    opacity: 1;
    position: relative;
    z-index: 1;
  }

  fieldset legend {
    transition: none !important;
  }
}

.select-persist-placeholder {
  > .v-input__control {
    > .v-input__slot {
      label {
        background-color: transparent;
        transform: translate(-6px, -26px) scale(0.75);
        pointer-events: auto;
      }
    }
  }
}

.chip-select-content {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 90%;
}

.chip-select-line {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.select-text-color .v-select__selection {
  color: var(--select-text-color) !important;
}
</style>
